import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { newDraftKey } from "../../global/draftKey";
import CircularProgress from "@mui/material/CircularProgress";
import Swal from "sweetalert2";
import {
  globalStyle,
  displayTwoDecimalDigits,
  formatId,
} from "../../global/Utils";
import { round2, formatTwoDecimals } from "../../global/money";
import { itemDisplayName } from "../../global/itemDisplay";
import { getCompany } from "../../Actions/CompanyAction";
import { getBuyer } from "../../Actions/BuyerAction";
import { getItem } from "../../Actions/ItemAction";
import { getUserList } from "../../Actions/UserAction";
import {
  addPosInvoice,
  getPosInvoiceNumber,
} from "../../Actions/PosInvoiceAction";
import { InputField, MoneyInputField } from "../Common/InputField";

import { upperCaseInput } from "../../global/textCase";
import { SearchableSelect } from "../Common/SearchableSelect";
// FBR / PRA POS payment modes
const paymentModes = [
  { value: 1, label: "Cash" },
  { value: 2, label: "Card" },
  { value: 3, label: "Gift Voucher" },
  { value: 4, label: "Loyalty Card" },
  { value: 5, label: "Mixed / Others" },
  { value: 6, label: "Cheque" },
];

// Sales tax is driven by how the customer paid: card settlements are charged the
// reduced rate, every other mode the standard rate. The server applies the same
// rule when it fiscalizes, so these two must stay in step.
// ItemCode is compulsory for PRA, so an item with no serial of its own falls
// back to this. The server applies the same default.
const DEFAULT_ITEM_CODE = "IT_1011";

const CARD_PAYMENT_MODE = 2;
const STANDARD_TAX_RATE = 16;
const CARD_TAX_RATE = 8;

const taxRateForPaymentMode = (paymentMode) =>
  Number(paymentMode) === CARD_PAYMENT_MODE ? CARD_TAX_RATE : STANDARD_TAX_RATE;

// InvoiceType 1 = new sale, 3 = return against an earlier USIN (RefUSIN)
const invoiceTypes = [
  { value: 1, label: "New Invoice" },
  { value: 3, label: "Return Invoice" },
];

const RETURN_INVOICE_TYPE = 3;

const defaultItem = {
  itemId: "",
  itemCode: "",
  itemName: "",
  pctCode: "",
  quantity: 1,
  unitPrice: 0,
  taxRate: 0,
  saleValue: 0,
  discount: 0,
  furtherTax: 0,
  taxCharged: 0,
  totalAmount: 0,
};

// A fresh row always carries the rate of the currently selected payment mode.
const makeItem = (paymentMode) => ({
  ...defaultItem,
  taxRate: taxRateForPaymentMode(paymentMode),
});

const toNumber = (value) => parseFloat(value) || 0;

// BuyerPNTN is sent as "1234567-8"
const formatPNTN = (value) => {
  const digits = String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 8);
  return digits.length > 7 ? `${digits.slice(0, 7)}-${digits.slice(7)}` : digits;
};

// BuyerPhoneNumber is sent as "0000-0000000"
const formatPhoneNumber = (value) => {
  const digits = String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 11);
  return digits.length > 4 ? `${digits.slice(0, 4)}-${digits.slice(4)}` : digits;
};

// Local "now" as the datetime-local input wants it ("YYYY-MM-DDTHH:mm").
// toISOString() is not usable here — it would shift the value to UTC.
const currentDateTimeLocal = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate(),
  )}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

// datetime-local gives "YYYY-MM-DDTHH:mm", the POS payload wants "YYYY-MM-DD HH:mm:ss"
const formatDateTime = (value) => {
  if (!value) return "";
  const [date, time = "00:00"] = String(value).split("T");
  return `${date} ${time.length === 5 ? `${time}:00` : time}`;
};

// SaleValue / TaxCharged / TotalAmount always follow qty, price, discount and rate
const recalculateItem = (item) => {
  const saleValue = round2(toNumber(item.quantity) * toNumber(item.unitPrice));
  const discount = toNumber(item.discount);
  const furtherTax = toNumber(item.furtherTax);
  const taxCharged = round2(
    ((saleValue - discount) * toNumber(item.taxRate)) / 100,
  );
  return {
    ...item,
    saleValue,
    taxCharged,
    totalAmount: round2(saleValue - discount + taxCharged + furtherTax),
  };
};

const AddPosInvoice = () => {
  const dispatch = useDispatch();
  // Sent with every Save of this draft, so Save pressed again returns the POS
  // invoice already saved instead of filing it twice (global/draftKey.js).
  const draftKey = useRef(newDraftKey());
  const formRef = React.useRef(null);

  const { userDetails } = useSelector((state) => state?.HeaderReducer);
  const { userList, isGettingUserList } = useSelector(
    (state) => state.UserReducer,
  );
  const companiesList = useSelector(
    (state) => state.CompanyListReducer?.companiesList || [],
  );
  const buyerList = useSelector((state) => state.BuyerReducer?.buyerList || []);
  const isAddingPosInvoice = useSelector(
    (state) => state.PosInvoiceReducer?.isAddingPosInvoice,
  );
  const itemList = useSelector(
    (state) => state.ItemListReducer?.itemList || [],
  );

  const [itemListAccToCompany, setItemListAccToCompany] = useState([]);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    userId: "",
    companyId: "",
    buyerId: "",
    invoiceNumber: "",
    posId: "",
    usin: "",
    dateTime: currentDateTimeLocal(),
    buyerPNTN: "",
    buyerCNIC: "",
    buyerName: "",
    buyerPhoneNumber: "",
    paymentMode: 1,
    invoiceType: 1,
    refUSIN: "",
    items: [makeItem(1)],
  });

  const isReturnInvoice = Number(formData.invoiceType) === RETURN_INVOICE_TYPE;

  const companyListAccToUser =
    userDetails?.role === "admin"
      ? companiesList?.filter(
          (item) => item?.createdBy?._id === formData?.userId,
        )
      : companiesList;

  const buyerListAccToUser = buyerList?.filter(
    (item) =>
      item?.createdBy?._id ===
      companiesList?.find((company) => company?._id === formData?.companyId)
        ?.createdBy?._id,
  );
  // Its "Concat serial number with Product Name" setting decides how the item
  // dropdown names each item.
  const invoiceCompany = companiesList?.find(
    (company) => company?._id === formData?.companyId,
  );

  function getCompanyShortCode(name) {
    if (!name) return "";

    const ignoreWords = ["and", "ltd", "inc", "corp", "company"];

    return name
      .split(" ")
      .filter((word) => word && !ignoreWords.includes(word.toLowerCase()))
      .map((word) => word[0].toUpperCase())
      .join("");
  }

  /* ------------------ INVOICE TOTALS ------------------ */
  const totals = useMemo(() => {
    const sum = (key) =>
      round2(formData.items.reduce((acc, item) => acc + toNumber(item[key]), 0));
    return {
      totalQuantity: sum("quantity"),
      totalSaleValue: sum("saleValue"),
      totalTaxCharged: sum("taxCharged"),
      discount: sum("discount"),
      furtherTax: sum("furtherTax"),
      totalBillAmount: sum("totalAmount"),
    };
  }, [formData.items]);

  /* ------------------ HANDLE INPUT CHANGE ------------------ */
  const handleChange = (e) => {
    const { name, value } = upperCaseInput(e.target);

    // HANDLE ITEMS ARRAY
    if (name.startsWith("items")) {
      const match = name.match(/items\[(\d+)\]\.(.+)/);
      const index = Number(match[1]);
      const field = match[2];

      const updatedItems = [...formData.items];
      updatedItems[index] = { ...updatedItems[index], [field]: value };

      if (field === "itemId") {
        const selectedItem = itemListAccToCompany.find(
          (item) => item._id === value,
        );
        updatedItems[index] = {
          ...updatedItems[index],
          itemCode: selectedItem?.serial || DEFAULT_ITEM_CODE,
          itemName: selectedItem?.name || "",
          pctCode: selectedItem?.HSCodeId?.pctCode || "",
          unitPrice: toNumber(selectedItem?.unitPrice),
          // The item's own sale tax is ignored: PRA POS invoices are rated by
          // payment mode, so every line carries the same derived rate.
          taxRate: taxRateForPaymentMode(formData.paymentMode),
          furtherTax: toNumber(selectedItem?.furtherTax),
        };
      }

      if (field === "quantity") {
        updatedItems[index] = {
          ...updatedItems[index],
          quantity: value === "" ? "" : Math.max(Number(value), 0),
        };
      }

      setFormData((prev) => ({
        ...prev,
        items: updatedItems.map((item, i) =>
          i === index ? recalculateItem(item) : item,
        ),
      }));
      setErrors((prev) => ({
        ...prev,
        items: {
          ...prev.items,
          [index]: { ...prev.items?.[index], [field]: "" },
        },
      }));
      return;
    }

    // BUYER — fills the four buyer payload fields, all still editable afterwards
    if (name === "buyerId") {
      const selectedBuyer = buyerListAccToUser?.find(
        (item) => item._id === value,
      );
      const registrationDigits = String(selectedBuyer?.registrationNo ?? "")
        .replace(/\D/g, "");
      const cnicDigits =
        registrationDigits.length === 13
          ? registrationDigits
          : String(selectedBuyer?.cnic ?? "").replace(/\D/g, "");

      setFormData((prev) => ({
        ...prev,
        buyerId: value,
        buyerName: selectedBuyer?.businessName || "",
        buyerPhoneNumber: formatPhoneNumber(selectedBuyer?.contact),
        buyerPNTN:
          registrationDigits.length && registrationDigits.length !== 13
            ? formatPNTN(registrationDigits)
            : "",
        buyerCNIC:
          cnicDigits.length === 13 ? formatId(cnicDigits, "CNIC") : "",
      }));
      setErrors((prev) => ({ ...prev, buyerId: "" }));
      return;
    }

    // COMPANY — invoice number, POS ID and the selectable items follow the company
    if (name === "companyId") {
      const companyNameRow = companyListAccToUser.find(
        (item) => item._id === value,
      );
      setItemListAccToCompany(
        itemList.filter((item) => item?.companyId?._id === value),
      );
      setFormData((prev) => ({
        ...prev,
        companyId: value,
        posId: companyNameRow?.posId || "",
        buyerId: "",
        buyerName: "",
        buyerPhoneNumber: "",
        buyerPNTN: "",
        buyerCNIC: "",
        invoiceNumber: "",
        items: [makeItem(prev.paymentMode)],
      }));
      setErrors((prev) => ({ ...prev, companyId: "", posId: "" }));
      if (value !== "") {
        dispatch(
          getPosInvoiceNumber(
            value,
            setFormData,
            getCompanyShortCode(companyNameRow?.companyName),
          ),
        );
      }
      return;
    }

    // PAYMENT MODE — changes the tax rate, so every line is re-rated and the
    // totals follow from the recalculated items.
    if (name === "paymentMode") {
      const nextTaxRate = taxRateForPaymentMode(value);
      setFormData((prev) => ({
        ...prev,
        paymentMode: value,
        items: prev.items.map((item) =>
          recalculateItem({ ...item, taxRate: nextTaxRate }),
        ),
      }));
      setErrors((prev) => ({ ...prev, paymentMode: "" }));
      return;
    }

    // USER — everything below the user selection is company scoped, so it resets
    if (name === "userId") {
      setItemListAccToCompany([]);
      setFormData((prev) => ({
        ...prev,
        userId: value,
        companyId: "",
        posId: "",
        buyerId: "",
        buyerName: "",
        buyerPhoneNumber: "",
        buyerPNTN: "",
        buyerCNIC: "",
        invoiceNumber: "",
        items: [makeItem(prev.paymentMode)],
      }));
      setErrors((prev) => ({ ...prev, userId: "" }));
      return;
    }

    const formattedValue =
      name === "buyerPNTN"
        ? formatPNTN(value)
        : name === "buyerCNIC"
          ? formatId(value, "CNIC")
          : name === "buyerPhoneNumber"
            ? formatPhoneNumber(value)
            : value;

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, makeItem(prev.paymentMode)],
    }));
  };

  const removeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items:
        prev.items.length === 1
          ? [makeItem(prev.paymentMode)]
          : prev.items.filter((_, i) => i !== index),
    }));
  };

  /* ------------------ VALIDATION ------------------ */
  const validate = () => {
    const newErrors = {};

    const requiredFields = [
      userDetails?.role === "admin" && "userId",
      "companyId",
      "buyerId",
      "posId",
      "usin",
      "dateTime",
      "buyerName",
      "paymentMode",
      "invoiceType",
      isReturnInvoice && "refUSIN",
    ].filter(Boolean);

    requiredFields.forEach((field) => {
      if (!String(formData[field] ?? "").trim()) {
        newErrors[field] = "This field is required";
      }
    });

    if (formData.posId && !(Number(formData.posId) > 0)) {
      newErrors.posId = "POS ID must be greater than 0";
    }

    if (!formData.buyerPNTN && !formData.buyerCNIC) {
      newErrors.buyerCNIC = "Enter either Buyer PNTN or Buyer CNIC";
    }

    formData.items.forEach((item, index) => {
      const itemErrors = {};
      if (!item.itemId) itemErrors.itemId = "Required";
      if (!(toNumber(item.quantity) > 0))
        itemErrors.quantity = "Must be greater than 0";
      if (!(toNumber(item.unitPrice) > 0))
        itemErrors.unitPrice = "Must be greater than 0";
      if (toNumber(item.discount) > toNumber(item.saleValue))
        itemErrors.discount = "Cannot exceed the sale value";

      if (Object.keys(itemErrors).length) {
        newErrors.items = newErrors.items || {};
        newErrors.items[index] = itemErrors;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ------------------ PAYLOAD ------------------ */
  // Our own API shape, not PRA's. The server re-rates every line from the
  // payment mode and maps this onto the PRA invoice model before posting, so
  // the token and the fiscalization stay server side.
  const buildPayload = () => {
    const invoiceType = Number(formData.invoiceType);
    const refUSIN = isReturnInvoice ? formData.refUSIN.trim() : null;

    return {
      companyId: formData.companyId,
      buyerId: formData.buyerId,
      ...(userDetails?.role === "admin" && { userId: formData.userId }),
      platform: "web",

      invoiceNumber: formData.invoiceNumber,
      posId: Number(formData.posId) || 0,
      usin: formData.usin,
      dateTime: formatDateTime(formData.dateTime),
      refUSIN,
      invoiceType,
      paymentMode: Number(formData.paymentMode),

      buyerPNTN: formData.buyerPNTN,
      buyerCNIC: formData.buyerCNIC,
      buyerName: formData.buyerName,
      buyerPhoneNumber: formData.buyerPhoneNumber,

      items: formData.items.map((item) => ({
        itemId: item.itemId,
        itemCode: item.itemCode,
        itemName: item.itemName,
        pctCode: item.pctCode,
        quantity: round2(item.quantity),
        unitPrice: round2(item.unitPrice),
        discount: round2(item.discount),
        furtherTax: round2(item.furtherTax),
      })),
    };
  };

  /* ------------------ RESET ------------------ */
  const resetForm = () => {
    setItemListAccToCompany([]);
    setFormData({
      userId: "",
      companyId: "",
      buyerId: "",
      invoiceNumber: "",
      posId: "",
      usin: "",
      dateTime: currentDateTimeLocal(),
      buyerPNTN: "",
      buyerCNIC: "",
      buyerName: "",
      buyerPhoneNumber: "",
      paymentMode: 1,
      invoiceType: 1,
      refUSIN: "",
      items: [makeItem(1)],
    });
    setErrors({});
  };

  /* ------------------ PRA RESULT DIALOGS ------------------ */
  const showPraSuccess = (title, praResponse) => {
    Swal.fire({
      title,
      html: `<p style="margin:0">${praResponse?.Response || "Invoice received successfully"}</p>`,
      icon: "success",
      confirmButtonText: "OK",
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = "2000";
      },
    });
  };

  const showPraError = (message, praResponse) => {
    const praErrors = praResponse?.Errors;
    Swal.fire({
      title: "PRA Error",
      html: `<p style="margin:0 0 8px">${message}</p>${
        praErrors
          ? `<pre style="text-align:left;max-height:40vh;overflow:auto;font-size:12px">${JSON.stringify(
              praErrors,
              null,
              2,
            )}</pre>`
          : ""
      }`,
      icon: "error",
      width: 700,
      confirmButtonText: "OK",
      didOpen: () => {
        const container = Swal.getContainer();
        if (container) container.style.zIndex = "2000";
      },
    });
  };

  /* ------------------ SUBMIT ------------------ */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      setTimeout(() => {
        const firstError = document.querySelector(".border-red-500");
        if (firstError) {
          firstError.scrollIntoView({ behavior: "smooth", block: "center" });
          firstError.focus();
        }
      }, 100);
      return;
    }

    dispatch(
      addPosInvoice(buildPayload(), resetForm, showPraSuccess, showPraError, {
        idempotencyKey: draftKey.current,
        onSaved: () => {
          draftKey.current = newDraftKey();
        },
      }),
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getCompany("", "", ""));
      dispatch(getBuyer("", "", ""));
      dispatch(getItem("", "", ""));
      dispatch(getUserList("", "", "", ""));
    }, 1000);
    return () => clearTimeout(timer);
  }, [dispatch]);

  // Check For Already Selected Items
  const selectedItemIds = formData.items
    .map((item) => item.itemId)
    .filter(Boolean);

  return (
    <div className="w-full min-h-screen flex justify-center py-4">
      <form
        ref={formRef}
        className="w-full space-y-6"
      >
        <h2
          className="text-center font-bold text-xl"
          style={{
            fontFamily: globalStyle.fontFamilyHeading,
            color: globalStyle.curveDarkColor,
          }}
        >
          Add POS Invoice
        </h2>

        {/* SELECT USER (ADMIN ONLY) */}
        {userDetails?.role === "admin" &&
          (isGettingUserList ? (
            <div className="flex justify-center items-center h-20">
              <CircularProgress />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <label className="block mb-1 font-medium">
                Select User<span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                name="userId"
                value={formData?.userId}
                onChange={handleChange}
                disabled={!userList || userList.length === 0}
                className={`w-full px-4 py-2 border rounded-md ${
                  errors.userId ? "border-red-500" : "border-gray-300"
                }`}
              >
                {!userList || userList.length === 0 ? (
                  <option value="">No Users Available</option>
                ) : (
                  <>
                    <option value="">SELECT</option>
                    {userList.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.email}
                      </option>
                    ))}
                  </>
                )}
              </SearchableSelect>
              {errors.userId && (
                <p className="text-red-500 text-sm mt-1">{errors.userId}</p>
              )}
            </div>
          ))}

        {(userDetails?.role !== "admin" || !!formData?.userId) && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Company Name */}
              <div>
                <label className="block mb-1 font-medium">
                  Company Name<span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  name="companyId"
                  value={formData.companyId}
                  onChange={handleChange}
                  disabled={
                    !companyListAccToUser || companyListAccToUser.length === 0
                  }
                  className={`uppercase w-full px-4 py-2 border rounded-md ${
                    errors.companyId ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  {!companyListAccToUser ||
                  companyListAccToUser.length === 0 ? (
                    <option value="">No Companies Available</option>
                  ) : (
                    <>
                      <option value="">Select</option>
                      {companyListAccToUser.map((company) => (
                        <option key={company._id} value={company._id}>
                          {company.companyName} / {company?.ntn}
                        </option>
                      ))}
                    </>
                  )}
                </SearchableSelect>
                {errors.companyId && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.companyId}
                  </p>
                )}
              </div>

              {formData.companyId && (
                <>
                  {/* Invoice Number */}
                  <InputField
                    label="Invoice Number"
                    name="invoiceNumber"
                    value={formData.invoiceNumber}
                    error={errors.invoiceNumber}
                    onChange={handleChange}
                    disabled={true}
                  />
                  {/* POS ID */}
                  <InputField
                    label="POS ID"
                    name="posId"
                    type="number"
                    min={0}
                    value={formData.posId}
                    error={errors.posId}
                    onChange={handleChange}
                    required
                  />
                  {/* USIN */}
                  <InputField
                    label="USIN"
                    name="usin"
                    value={formData.usin}
                    error={errors.usin}
                    onChange={handleChange}
                    required
                  />
                  {/* Date & Time */}
                  <InputField
                    label="Invoice Date & Time"
                    name="dateTime"
                    type="datetime-local"
                    value={formData.dateTime}
                    error={errors.dateTime}
                    onChange={handleChange}
                    required
                  />
                  {/* Invoice Type */}
                  <div>
                    <label className="block mb-1 font-medium">
                      Invoice Type<span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      name="invoiceType"
                      value={formData.invoiceType}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-md ${
                        errors.invoiceType ? "border-red-500" : "border-gray-300"
                      }`}
                    >
                      {invoiceTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </SearchableSelect>
                    {errors.invoiceType && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.invoiceType}
                      </p>
                    )}
                  </div>
                  {/* Payment Mode */}
                  <div>
                    <label className="block mb-1 font-medium">
                      Payment Mode<span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      name="paymentMode"
                      value={formData.paymentMode}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-md ${
                        errors.paymentMode
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    >
                      {paymentModes.map((mode) => (
                        <option key={mode.value} value={mode.value}>
                          {mode.label}
                        </option>
                      ))}
                    </SearchableSelect>
                    {errors.paymentMode ? (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.paymentMode}
                      </p>
                    ) : (
                      <p className="text-gray-500 text-sm mt-1">
                        Sales tax applied to every item:{" "}
                        <b>{taxRateForPaymentMode(formData.paymentMode)}%</b>
                      </p>
                    )}
                  </div>
                  {/* Ref USIN (returns only) */}
                  {isReturnInvoice && (
                    <InputField
                      label="Ref. USIN"
                      name="refUSIN"
                      value={formData.refUSIN}
                      error={errors.refUSIN}
                      onChange={handleChange}
                      required
                    />
                  )}
                </>
              )}
            </div>

            {formData.companyId && (
              <>
                {/* BUYER DETAILS */}
                <h2
                  className="text-left font-bold text-xl"
                  style={{
                    fontFamily: globalStyle.fontFamilyHeading,
                    color: globalStyle.curveDarkColor,
                  }}
                >
                  Buyer Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Buyer */}
                  <div>
                    <label className="block mb-1 font-medium">
                      Buyer<span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      name="buyerId"
                      value={formData.buyerId}
                      onChange={handleChange}
                      disabled={
                        !buyerListAccToUser || buyerListAccToUser.length === 0
                      }
                      className={`uppercase w-full px-4 py-2 border rounded-md ${
                        errors.buyerId ? "border-red-500" : "border-gray-300"
                      }`}
                    >
                      {!buyerListAccToUser ||
                      buyerListAccToUser.length === 0 ? (
                        <option value="">No Buyer Available</option>
                      ) : (
                        <>
                          <option value="">Select</option>
                          {buyerListAccToUser.map((buyer) => (
                            <option key={buyer._id} value={buyer._id}>
                              {buyer.businessName}
                            </option>
                          ))}
                        </>
                      )}
                    </SearchableSelect>
                    {errors.buyerId && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.buyerId}
                      </p>
                    )}
                  </div>
                  {/* Buyer Name */}
                  <InputField
                    label="Buyer Name"
                    name="buyerName"
                    value={formData.buyerName}
                    error={errors.buyerName}
                    onChange={handleChange}
                    required
                  />
                  {/* Buyer Phone Number */}
                  <InputField
                    label="Buyer Phone Number"
                    name="buyerPhoneNumber"
                    value={formData.buyerPhoneNumber}
                    error={errors.buyerPhoneNumber}
                    onChange={handleChange}
                  />
                  {/* Buyer PNTN */}
                  <InputField
                    label="Buyer PNTN"
                    name="buyerPNTN"
                    value={formData.buyerPNTN}
                    error={errors.buyerPNTN}
                    onChange={handleChange}
                  />
                  {/* Buyer CNIC */}
                  <InputField
                    label="Buyer CNIC"
                    name="buyerCNIC"
                    value={formData.buyerCNIC}
                    error={errors.buyerCNIC}
                    onChange={handleChange}
                  />
                </div>

                {/* INVOICE ITEMS */}
                <h2
                  className="text-left font-bold text-xl"
                  style={{
                    fontFamily: globalStyle.fontFamilyHeading,
                    color: globalStyle.curveDarkColor,
                  }}
                >
                  Invoice Items
                </h2>
                <div className="bg-gray-50 rounded-xl p-4 space-y-6">
                  {formData?.items?.map((itemValue, index) => (
                    <div
                      key={index}
                      className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
                    >
                      {/* HEADER */}
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-gray-700">
                          Item #{index + 1}
                        </h4>
                      </div>
                      {/* FIELDS */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Item List */}
                        <div>
                          <label className="block mb-1 font-medium text-gray-700">
                            Item <span className="text-red-500">*</span>
                          </label>
                          <SearchableSelect
                            name={`items[${index}].itemId`}
                            value={itemValue.itemId}
                            onChange={handleChange}
                            disabled={
                              !itemListAccToCompany ||
                              itemListAccToCompany.length === 0
                            }
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-200 focus:outline-none ${
                              errors?.items?.[index]?.itemId
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                          >
                            {!itemListAccToCompany ||
                            itemListAccToCompany.length === 0 ? (
                              <option value="">No Items Available</option>
                            ) : (
                              <>
                                <option value="">Select</option>
                                {itemListAccToCompany.map((item) => {
                                  const isAlreadySelected =
                                    selectedItemIds.includes(item._id) &&
                                    itemValue.itemId !== item._id;
                                  return (
                                    <option
                                      key={item._id}
                                      value={item._id}
                                      disabled={isAlreadySelected}
                                    >
                                      {itemDisplayName(item, invoiceCompany)}
                                    </option>
                                  );
                                })}
                              </>
                            )}
                          </SearchableSelect>
                          {errors?.items?.[index]?.itemId && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.items[index].itemId}
                            </p>
                          )}
                        </div>
                        {/* Item Code */}
                        <InputField
                          label="Item Code"
                          name={`items[${index}].itemCode`}
                          value={itemValue.itemCode}
                          error={errors?.items?.[index]?.itemCode}
                          onChange={handleChange}
                        />
                        {/* Item Name */}
                        <InputField
                          label="Item Name"
                          name={`items[${index}].itemName`}
                          value={itemValue.itemName}
                          error={errors?.items?.[index]?.itemName}
                          onChange={handleChange}
                        />
                        {/* PCT Code */}
                        <InputField
                          label="PCT Code"
                          name={`items[${index}].pctCode`}
                          value={itemValue.pctCode}
                          error={errors?.items?.[index]?.pctCode}
                          onChange={handleChange}
                        />
                        {/* Unit Price */}
                        <MoneyInputField
                          label="Unit Price"
                          min={0}
                          name={`items[${index}].unitPrice`}
                          value={itemValue.unitPrice}
                          error={errors?.items?.[index]?.unitPrice}
                          onChange={handleChange}
                          required
                        />
                        {/* Quantity */}
                        <InputField
                          label="Quantity"
                          type="number"
                          min={0}
                          name={`items[${index}].quantity`}
                          value={itemValue.quantity}
                          error={errors?.items?.[index]?.quantity}
                          onChange={handleChange}
                          required
                        />
                        {/* Sale Value */}
                        <InputField
                          label="Sale Value"
                          type="number"
                          name={`items[${index}].saleValue`}
                          value={displayTwoDecimalDigits(itemValue.saleValue)}
                          error={errors?.items?.[index]?.saleValue}
                          readOnly={true}
                        />
                        {/* Tax Rate — derived from the payment mode */}
                        <InputField
                          label="Tax Rate %"
                          type="number"
                          name={`items[${index}].taxRate`}
                          value={itemValue.taxRate}
                          error={errors?.items?.[index]?.taxRate}
                          readOnly={true}
                        />
                        {/* Discount */}
                        <MoneyInputField
                          label="Discount"
                          min={0}
                          name={`items[${index}].discount`}
                          value={itemValue.discount}
                          error={errors?.items?.[index]?.discount}
                          onChange={handleChange}
                        />
                        {/* Further Tax */}
                        <MoneyInputField
                          label="Further Tax"
                          min={0}
                          name={`items[${index}].furtherTax`}
                          value={itemValue.furtherTax}
                          error={errors?.items?.[index]?.furtherTax}
                          onChange={handleChange}
                        />
                        {/* Tax Charged */}
                        <InputField
                          label="Tax Charged"
                          type="number"
                          name={`items[${index}].taxCharged`}
                          value={displayTwoDecimalDigits(itemValue.taxCharged)}
                          error={errors?.items?.[index]?.taxCharged}
                          readOnly={true}
                        />
                        {/* Total Amount */}
                        <InputField
                          label="Total Amount"
                          type="number"
                          name={`items[${index}].totalAmount`}
                          value={displayTwoDecimalDigits(itemValue.totalAmount)}
                          error={errors?.items?.[index]?.totalAmount}
                          readOnly={true}
                        />
                      </div>

                      {/* ACTION BUTTONS (ONLY LAST ITEM) */}
                      <div className="flex justify-end gap-3 mt-6">
                        {index === formData.items.length - 1 && (
                          <button
                            type="button"
                            onClick={addItem}
                            style={{ backgroundColor: globalStyle.blueColor }}
                            className="px-5 py-2 text-white rounded-lg font-medium hover:opacity-90 transition"
                          >
                            + Add Item
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          style={{ backgroundColor: globalStyle.blueColor }}
                          className="px-5 py-2 text-white rounded-lg font-medium hover:opacity-90 transition"
                        >
                          − Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* INVOICE TOTALS */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
                  {/* Left Section */}
                  <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Quantity</span>
                      <span className="font-medium">
                        {formatTwoDecimals(totals.totalQuantity)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Discount</span>
                      <span className="font-medium">
                        {formatTwoDecimals(totals.discount)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Further Tax</span>
                      <span className="font-medium">
                        {formatTwoDecimals(totals.furtherTax)}
                      </span>
                    </div>
                  </div>
                  {/* Right Section */}
                  <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Sale Value</span>
                      <span className="font-medium">
                        {formatTwoDecimals(totals.totalSaleValue)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Tax Charged</span>
                      <span className="font-medium">
                        {formatTwoDecimals(totals.totalTaxCharged)}
                      </span>
                    </div>
                    <div className="border-t pt-3 mt-2 flex justify-between items-center">
                      <span className="text-base font-semibold text-gray-800">
                        Total Bill Amount
                      </span>
                      <span className="text-lg font-bold text-green-600">
                        {formatTwoDecimals(totals.totalBillAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* SUBMIT */}
        <div className="w-full flex justify-center">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isAddingPosInvoice}
            className="w-full md:w-1/3 py-2 rounded-md text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: globalStyle.buttonColor }}
          >
            {isAddingPosInvoice ? "POSTING TO PRA..." : "SAVE POS INVOICE"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddPosInvoice;
